import type { BadgeTone } from '@/ui/kit';

import {
  MASKED,
  MISSING,
  flatten,
  formatDuration,
  formatScalar,
  isRecord,
  listOf,
  numberOf,
  rawJson,
  sectionOf,
  stringOf,
  type PayloadRecord,
  type Scalar,
  type Section,
} from './payload';

/**
 * Presentateurs : ce que l'exploitant lit d'une charge utile.
 *
 * Quatre collections ont un presentateur dedie — Alimentation, Tirages, Lots de
 * lait, Mouvements, celles qu'on ouvre pour comprendre un incident — et les six
 * autres un rendu generique cle / valeur. Un presentateur **ne leve jamais** : il
 * ne lit que par `payload.ts`, un champ absent ou d'un autre type devient « — »,
 * et une forme sans enveloppe reconnue (`recognised: false`) laisse l'ecran
 * afficher le JSON brut. `presentRecord` ajoute une ceinture : meme une erreur
 * inattendue dans un presentateur ne casse pas la page.
 *
 * Les formes lues ici sont **copiees du mobile** (`Local*Service`, schemas Zod de
 * `mobile/src/domains/<domaine>/domain`), pas devinees : le serveur ne valide
 * rien, une forme inventee serait acceptee en silence et montree comme vraie.
 *
 * Le texte libre (`comment`, `note`…) n'est jamais lu pour un role `support` :
 * le serveur l'a retire et en donne les chemins (`redactedFields`). Le champ est
 * alors rendu « Masqué pour votre rôle » — jamais « — », qui dirait qu'il est vide.
 */

export { MASKED };

export interface Summary {
  /** Instant de l'evenement, horloge de l'APPAREIL ; `null` : l'ecran retombe sur la reception. */
  readonly when: string | null;
  readonly nature: { readonly label: string; readonly tone: BadgeTone } | null;
  /** Une ligne ; jamais vide. */
  readonly detail: string;
}

export interface DetailRow {
  readonly label: string;
  readonly value: string;
  readonly mono?: boolean;
  readonly masked?: boolean;
}

export interface Presentation {
  /** Faux : aucune enveloppe connue, l'ecran montre le JSON brut et le dit. */
  readonly recognised: boolean;
  readonly summary: Summary;
  readonly rows: readonly DetailRow[];
}

// ---------------------------------------------------------------------------
// Libelles
// ---------------------------------------------------------------------------

const FIELD_LABELS: Readonly<Record<string, string>> = {
  clientId: 'Identifiant',
  babyClientId: 'Profil ou compte',
  recordedByClientId: 'Saisi par (compte)',
  createdAt: 'Créé le (appareil)',
  updatedAt: 'Modifié le (appareil)',
  version: 'Version locale',
  deletedAt: 'Supprimé sur l’appareil le',
  idempotencyKey: 'Clé d’idempotence',
  name: 'Nom',
  firstName: 'Prénom',
  lastName: 'Nom de famille',
  birthDate: 'Date de naissance',
  sex: 'Sexe',
  dueDate: 'Date prévue',
  birthWeightGrams: 'Poids de naissance',
  birthHeightMillimeters: 'Taille de naissance',
  birthHeadCircumferenceMillimeters: 'Périmètre crânien de naissance',
  archivedAt: 'Archivé le',
  category: 'Catégorie',
  status: 'Statut',
  measuredAt: 'Mesuré le',
  weightGrams: 'Poids',
  heightMillimeters: 'Taille',
  headCircumferenceMillimeters: 'Périmètre crânien',
  source: 'Origine',
  eatenAt: 'Mangé le',
  type: 'Type',
  foods: 'Aliments',
  offeredQuantity: 'Quantité proposée',
  consumedQuantity: 'Quantité mangée',
  offeredUnit: 'Unité',
  texture: 'Texture',
  preparation: 'Préparation',
  reactionType: 'Type de réaction',
  reactionSeverity: 'Gravité de la réaction',
  requiresMedicalAttention: 'Avis médical à demander',
  appreciation: 'Appréciation',
  comment: 'Commentaire',
  note: 'Note',
  notes: 'Notes',
  allergens: 'Allergènes',
  indicativeAgeMonths: 'Âge indicatif (mois)',
  firstIntroducedAt: 'Première introduction',
  presentationsCount: 'Présentations',
  preference: 'Avis du bébé',
  custom: 'Ajouté par la famille',
  location: 'Lieu',
  description: 'Description',
  physicalLocation: 'Emplacement physique',
  temperatureCelsius: 'Température',
  sortOrder: 'Ordre d’affichage',
  favorite: 'Favori',
  perceivedQuality: 'Qualité perçue',
  reaction: 'Réaction',
  formulaMilkName: 'Lait artificiel',
};

const labelOf = (key: string): string => FIELD_LABELS[key] ?? key;

const MILK_TYPES: Readonly<Record<string, string>> = {
  breast_milk: 'lait maternel',
  formula: 'lait artificiel',
  mixed: 'lait mixte',
  water: 'eau',
  other: 'autre',
};

const BREAST_ORDERS: Readonly<Record<string, string>> = {
  left: 'Gauche',
  right: 'Droite',
  both: 'Les deux',
  both_left_first: 'Les deux, gauche d’abord',
  both_right_first: 'Les deux, droit d’abord',
};

const CATEGORIES: Readonly<Record<string, string>> = {
  milk: 'Lait',
  puree: 'Purée de légumes',
  complete_meal: 'Repas complet',
  meat: 'Viande',
  compote: 'Compote',
  other: 'Autre',
};

const BATCH_STATUSES: Readonly<Record<string, string>> = {
  available: 'Disponible',
  split: 'Réparti',
  merged: 'Fusionné',
  consumed: 'Consommé',
  archived: 'Archivé',
};

const MOVEMENT_TYPES: Readonly<Record<string, { label: string; tone: BadgeTone }>> = {
  creation: { label: 'Création', tone: 'success' },
  move: { label: 'Déplacement', tone: 'info' },
  split: { label: 'Répartition', tone: 'info' },
  merge: { label: 'Fusion', tone: 'info' },
  consumption: { label: 'Consommation', tone: 'warning' },
  loss: { label: 'Perte', tone: 'danger' },
  correction: { label: 'Correction', tone: 'neutral' },
};

/**
 * Commentaires de mouvement qui sont des **codes** et non des phrases : le mobile
 * et le serveur ecrivent `feeding` sur le debit de stock d'un biberon. Le journal
 * est immuable (ADR 0011), le code se traduit donc a l'affichage ; tout autre
 * commentaire est un texte libre, rendu tel quel. Meme table que le mobile
 * (`stockMovementCommentLabels`), dont ce dossier ne partage aucun code.
 */
const MOVEMENT_COMMENT_LABELS: Readonly<Record<string, string>> = { feeding: 'Biberon' };

const movementComment = (value: unknown): string => {
  const text = String(value);

  return Object.hasOwn(MOVEMENT_COMMENT_LABELS, text) ? (MOVEMENT_COMMENT_LABELS[text] ?? text) : text;
};

const ORIGINS: Readonly<Record<string, string>> = { pumping: 'Tirage', manual: 'Saisie manuelle' };

const lookup = (table: Readonly<Record<string, string>>, value: string | null): string | null =>
  value === null ? null : (table[value] ?? value);

// ---------------------------------------------------------------------------
// Constructeur de lignes
// ---------------------------------------------------------------------------

/** Etat d'une presentation en cours : l'enveloppe lue, les chemins masques, ceux deja rendus. */
interface Context {
  readonly section: Section;
  readonly masked: ReadonlySet<string>;
  readonly used: Set<string>;
}

interface FieldOptions {
  readonly mono?: boolean;
  readonly format?: (value: unknown) => string;
}

const field = (ctx: Context, label: string, key: string, options: FieldOptions = {}): DetailRow => {
  const path = `${ctx.section.key}.${key}`;

  ctx.used.add(path);

  if (ctx.masked.has(path)) {
    return { label, value: MASKED, masked: true };
  }

  const raw = ctx.section.value[key];
  const value =
    raw === undefined || raw === null
      ? MISSING
      : options.format !== undefined
        ? options.format(raw)
        : typeof raw === 'object'
          ? rawJson(raw).replace(/\s+/g, ' ')
          : formatScalar(key, raw as Scalar);

  return { label, value, mono: options.mono };
};

/** Ligne de suppression logique, **seulement** quand l'appareil a supprime l'enregistrement. */
const deletion = (ctx: Context): readonly DetailRow[] =>
  stringOf(ctx.section.value, 'deletedAt') === null ? [] : [field(ctx, labelOf('deletedAt'), 'deletedAt')];

/** `90 ml`, `1 portion`, `3 portions` ; `withSign` ecrit le plus d'une variation (`+90 ml`). */
const quantity = (value: number | null, unit: string | null, withSign = false): string | null => {
  if (value === null) {
    return null;
  }

  const text = `${withSign && value > 0 ? '+' : ''}${value.toLocaleString('fr-FR')}`;

  if (unit === 'portion') {
    return `${text} ${Math.abs(value) === 1 ? 'portion' : 'portions'}`;
  }

  return unit === null || unit === 'ml' ? `${text} ml` : `${text} ${unit}`;
};

const joinParts = (parts: readonly (string | null)[]): string => {
  const kept = parts.filter((part): part is string => part !== null && part !== '');

  return kept.length === 0 ? MISSING : kept.join(' · ');
};

// ---------------------------------------------------------------------------
// Alimentation (`feeding_session`, `{session}`)
// ---------------------------------------------------------------------------

const usagesOf = (session: PayloadRecord): readonly PayloadRecord[] => (listOf(session, 'stockUsages') ?? []).filter(isRecord);

const feedingSummary = (session: PayloadRecord): Summary => {
  const type = stringOf(session, 'type');
  const when = stringOf(session, 'startedAt');

  if (type === 'bottle') {
    const drunk = numberOf(session, 'consumedQuantityMl') ?? numberOf(session, 'offeredQuantityMl');

    return {
      when,
      nature: { label: 'Biberon', tone: 'brand' },
      detail: joinParts([
        quantity(drunk, 'ml'),
        lookup(MILK_TYPES, stringOf(session, 'milkType')),
        usagesOf(session).length > 0 ? 'lot débité' : null,
      ]),
    };
  }

  if (type === 'breastfeeding') {
    const left = numberOf(session, 'leftDurationSeconds');
    const right = numberOf(session, 'rightDurationSeconds');

    return {
      when,
      nature: { label: 'Tétée', tone: 'info' },
      detail: joinParts([
        lookup(BREAST_ORDERS, stringOf(session, 'breastOrder')),
        left === null ? null : `gauche ${formatDuration(left)}`,
        right === null ? null : `droit ${formatDuration(right)}`,
      ]),
    };
  }

  return { when, nature: { label: type ?? 'Séance', tone: 'neutral' }, detail: MISSING };
};

const feedingRows = (ctx: Context): readonly DetailRow[] => {
  const usages = usagesOf(ctx.section.value);
  const usageText =
    usages.length === 0
      ? 'Aucun'
      : `${String(usages.length)} · ${usages.map((usage) => quantity(numberOf(usage, 'quantityMl'), 'ml') ?? MISSING).join(', ')}`;

  return [
    field(ctx, 'Type', 'type', { format: (v) => (v === 'bottle' ? 'Biberon' : v === 'breastfeeding' ? 'Tétée' : String(v)) }),
    field(ctx, 'Début', 'startedAt'),
    field(ctx, 'Fin', 'endedAt'),
    field(ctx, 'Quantité proposée', 'offeredQuantityMl'),
    field(ctx, 'Quantité bue', 'consumedQuantityMl'),
    field(ctx, 'Reste', 'remainingQuantityMl'),
    field(ctx, 'Type de lait', 'milkType', { format: (v) => lookup(MILK_TYPES, String(v)) ?? MISSING }),
    field(ctx, 'Côté', 'breastOrder', { format: (v) => lookup(BREAST_ORDERS, String(v)) ?? MISSING }),
    field(ctx, 'Durée sein gauche', 'leftDurationSeconds'),
    field(ctx, 'Durée sein droit', 'rightDurationSeconds'),
    { label: 'Lots débités', value: usageText },
    field(ctx, labelOf('perceivedQuality'), 'perceivedQuality'),
    field(ctx, labelOf('location'), 'location'),
    field(ctx, labelOf('reaction'), 'reaction'),
    field(ctx, labelOf('comment'), 'comment'),
    ...deletion(ctx),
    field(ctx, labelOf('clientId'), 'clientId', { mono: true }),
  ];
};

// ---------------------------------------------------------------------------
// Tirages (`pumping_session`, `{session}`)
// ---------------------------------------------------------------------------

const pumpingSummary = (session: PayloadRecord): Summary => {
  const left = numberOf(session, 'leftQuantityMl');
  const right = numberOf(session, 'rightQuantityMl');
  const total = numberOf(session, 'totalQuantityMl') ?? (left !== null && right !== null ? left + right : null);
  const duration = numberOf(session, 'durationSeconds');

  return {
    when: stringOf(session, 'pumpedAt'),
    nature: { label: 'Tirage', tone: 'success' },
    detail: joinParts([
      quantity(total, 'ml'),
      left === null && right === null ? null : `gauche ${String(left ?? 0)} + droit ${String(right ?? 0)}`,
      duration === null ? null : formatDuration(duration),
    ]),
  };
};

const pumpingRows = (ctx: Context): readonly DetailRow[] => [
  field(ctx, 'Tiré le', 'pumpedAt'),
  field(ctx, 'Quantité gauche', 'leftQuantityMl'),
  field(ctx, 'Quantité droite', 'rightQuantityMl'),
  field(ctx, 'Quantité totale', 'totalQuantityMl'),
  field(ctx, 'Durée', 'durationSeconds'),
  field(ctx, 'Sein de départ', 'startingSide', { format: (v) => lookup({ left: 'Gauche', right: 'Droite' }, String(v)) ?? MISSING }),
  field(ctx, 'Équipement', 'equipmentName'),
  field(ctx, 'Étiquettes', 'tags', { format: (v) => (Array.isArray(v) && v.length > 0 ? v.map(String).join(', ') : 'Aucune') }),
  field(ctx, labelOf('note'), 'note'),
  ...deletion(ctx),
  field(ctx, labelOf('clientId'), 'clientId', { mono: true }),
];

// ---------------------------------------------------------------------------
// Lots (`milk_batch`, `{batch}`, `{batch, movement}`, `{revision}`)
// ---------------------------------------------------------------------------

const batchSummary = (section: Section): Summary => {
  const batch = section.value;

  if (section.key === 'revision') {
    return {
      when: stringOf(batch, 'updatedAt'),
      nature: { label: 'Fiche modifiée', tone: 'neutral' },
      detail: 'Révision de la fiche : contenu, poids, date limite, note',
    };
  }

  const unit = stringOf(batch, 'unit');
  const category = stringOf(batch, 'category');
  const remaining = quantity(numberOf(batch, 'remainingQuantityMl'), unit);
  const initial = numberOf(batch, 'initialQuantityMl');

  return {
    when: stringOf(batch, 'pumpedAt') ?? stringOf(batch, 'createdAt'),
    nature: { label: lookup(CATEGORIES, category ?? 'milk') ?? 'Lait', tone: 'brand' },
    detail: joinParts([
      remaining === null ? null : initial === null ? `${remaining} restants` : `${remaining} sur ${quantity(initial, unit) ?? MISSING}`,
      lookup(BATCH_STATUSES, stringOf(batch, 'status')),
      stringOf(batch, 'contentLabel') ?? stringOf(batch, 'code'),
    ]),
  };
};

const batchRows = (ctx: Context): readonly DetailRow[] => {
  if (ctx.section.key === 'revision') {
    return [
      field(ctx, 'Contenu', 'contentLabel'),
      field(ctx, 'Poids d’une portion', 'portionWeightGrams'),
      field(ctx, 'Date limite', 'expiresAt'),
      field(ctx, 'Règle de conservation', 'expiryRuleCode'),
      field(ctx, 'Date limite forcée', 'expiryManuallyOverridden'),
      field(ctx, labelOf('comment'), 'comment'),
      field(ctx, 'Modifié le (appareil)', 'updatedAt'),
    ];
  }

  const unit = stringOf(ctx.section.value, 'unit');
  const amount = (value: unknown): string => quantity(typeof value === 'number' ? value : null, unit) ?? MISSING;

  return [
    field(ctx, 'Code', 'code', { mono: true }),
    field(ctx, 'Catégorie', 'category', { format: (v) => lookup(CATEGORIES, String(v)) ?? MISSING }),
    field(ctx, 'Contenu', 'contentLabel'),
    field(ctx, 'Statut', 'status', { format: (v) => lookup(BATCH_STATUSES, String(v)) ?? MISSING }),
    field(ctx, 'Quantité restante', 'remainingQuantityMl', { format: amount }),
    field(ctx, 'Quantité initiale', 'initialQuantityMl', { format: amount }),
    field(ctx, 'Poids d’une portion', 'portionWeightGrams'),
    field(ctx, 'Origine', 'origin', { format: (v) => lookup(ORIGINS, String(v)) ?? MISSING }),
    field(ctx, 'Date du lait (référence)', 'pumpedAt'),
    field(ctx, 'Date limite', 'expiresAt'),
    field(ctx, 'Règle de conservation', 'expiryRuleCode'),
    field(ctx, 'Date limite forcée', 'expiryManuallyOverridden'),
    field(ctx, 'Réfrigéré le', 'refrigeratedAt'),
    field(ctx, 'Congelé le', 'frozenAt'),
    field(ctx, 'Décongelé le', 'thawedAt'),
    field(ctx, 'Lot parent', 'parentBatchClientId', { mono: true }),
    field(ctx, 'Séance de tirage', 'pumpingSessionClientId', { mono: true }),
    field(ctx, 'Lieu de stockage', 'storageLocationClientId', { mono: true }),
    field(ctx, labelOf('comment'), 'comment'),
    ...deletion(ctx),
    field(ctx, labelOf('clientId'), 'clientId', { mono: true }),
  ];
};

// ---------------------------------------------------------------------------
// Mouvements (`stock_movement`, `{movement, affectedBatches}`)
// ---------------------------------------------------------------------------

const batchesOf = (payload: unknown): readonly PayloadRecord[] =>
  isRecord(payload) ? (listOf(payload, 'affectedBatches') ?? []).filter(isRecord) : [];

/** Unite du lot concerne : un mouvement ne la porte pas, son lot si. */
const movementUnit = (movement: PayloadRecord, payload: unknown): string | null => {
  const batches = batchesOf(payload);
  const target = stringOf(movement, 'batchClientId');
  const batch = batches.find((candidate) => stringOf(candidate, 'clientId') === target) ?? batches[0] ?? null;

  return batch === null ? null : stringOf(batch, 'unit');
};

const movementSummary = (movement: PayloadRecord, payload: unknown): Summary => {
  const type = stringOf(movement, 'type');
  const nature = type === null ? null : (MOVEMENT_TYPES[type] ?? { label: type, tone: 'neutral' as const });
  const unit = movementUnit(movement, payload);
  const delta = numberOf(movement, 'quantityDeltaMl');
  const batches = batchesOf(payload);
  const target = stringOf(movement, 'batchClientId');
  const code = stringOf(batches.find((candidate) => stringOf(candidate, 'clientId') === target) ?? null, 'code');

  return {
    when: stringOf(movement, 'occurredAt'),
    nature,
    detail: joinParts([quantity(delta, unit, true), code]),
  };
};

const movementRows = (ctx: Context, payload: unknown): readonly DetailRow[] => {
  const unit = movementUnit(ctx.section.value, payload);
  const batches = batchesOf(payload);
  const amount = (value: unknown): string => quantity(typeof value === 'number' ? value : null, unit) ?? MISSING;

  ctx.used.add('affectedBatches');

  return [
    field(ctx, 'Opération', 'type', { format: (v) => MOVEMENT_TYPES[String(v)]?.label ?? String(v) }),
    field(ctx, 'Quantité', 'quantityMl', { format: amount }),
    field(ctx, 'Variation du lot', 'quantityDeltaMl', {
      format: (v) => (typeof v === 'number' ? (quantity(v, unit, true) ?? MISSING) : MISSING),
    }),
    field(ctx, 'Survenu le (appareil)', 'occurredAt'),
    field(ctx, 'Lot', 'batchClientId', { mono: true }),
    field(ctx, 'Lieu d’origine', 'sourceStorageLocationClientId', { mono: true }),
    field(ctx, 'Lieu de destination', 'destinationStorageLocationClientId', { mono: true }),
    field(ctx, 'Lots liés', 'relatedBatchClientIds', { format: (v) => (Array.isArray(v) && v.length > 0 ? String(v.length) : 'Aucun') }),
    {
      label: 'Lots touchés',
      value:
        batches.length === 0
          ? MISSING
          : batches
              .map((batch) =>
                joinParts([
                  stringOf(batch, 'code'),
                  lookup(BATCH_STATUSES, stringOf(batch, 'status')),
                  quantity(numberOf(batch, 'remainingQuantityMl'), stringOf(batch, 'unit')),
                ]),
              )
              .join(' ; '),
    },
    field(ctx, labelOf('comment'), 'comment', { format: movementComment }),
    ...deletion(ctx),
    field(ctx, labelOf('idempotencyKey'), 'idempotencyKey', { mono: true }),
    field(ctx, labelOf('clientId'), 'clientId', { mono: true }),
  ];
};

// ---------------------------------------------------------------------------
// Rendu generique (six collections)
// ---------------------------------------------------------------------------

interface GenericSpec {
  readonly envelope: string;
  readonly whenKey: string | null;
  readonly detail: (record: PayloadRecord) => string;
}

const firstText = (record: PayloadRecord, keys: readonly string[]): string | null => {
  for (const key of keys) {
    const text = stringOf(record, key);

    if (text !== null) {
      return text;
    }
  }

  return null;
};

const GENERIC: Readonly<Record<string, GenericSpec>> = {
  solid_meal: {
    envelope: 'record',
    whenKey: 'eatenAt',
    detail: (r) => {
      const foods = listOf(r, 'foods');
      const eaten = numberOf(r, 'consumedQuantity');
      const unit = stringOf(r, 'offeredUnit');

      return joinParts([
        foods === null ? null : `${String(foods.length)} aliment${foods.length > 1 ? 's' : ''}`,
        eaten === null ? null : `${eaten.toLocaleString('fr-FR')} ${unit ?? ''}`.trim(),
      ]);
    },
  },
  diversification_food: {
    envelope: 'record',
    whenKey: 'createdAt',
    detail: (r) => joinParts([stringOf(r, 'name'), stringOf(r, 'category')]),
  },
  food_reaction: { envelope: 'record', whenKey: null, detail: (r) => firstText(r, ['type', 'severity', 'name']) ?? MISSING },
  growth_measurement: {
    envelope: 'measurement',
    whenKey: 'measuredAt',
    detail: (r) => {
      const weight = numberOf(r, 'weightGrams');
      const height = numberOf(r, 'heightMillimeters');
      const head = numberOf(r, 'headCircumferenceMillimeters');

      return joinParts([
        weight === null ? null : formatScalar('weightGrams', weight),
        height === null ? null : formatScalar('heightMillimeters', height),
        head === null ? null : `PC ${formatScalar('headCircumferenceMillimeters', head)}`,
      ]);
    },
  },
  storage_location: {
    envelope: 'location',
    whenKey: 'createdAt',
    detail: (r) => joinParts([stringOf(r, 'name'), stringOf(r, 'type')]),
  },
  baby_profile: {
    envelope: 'profile',
    whenKey: 'createdAt',
    detail: (r) => firstText(r, ['firstName', 'name']) ?? MISSING,
  },
};

/** Enveloppes ecartees des lignes generiques : comptabilite d'appareil sans interet pour l'exploitant. */
const HIDDEN = new Set(['serverId', 'syncStatus', 'lastSyncedAt']);

const genericRows = (ctx: Context, payload: unknown): readonly DetailRow[] => {
  const rows: DetailRow[] = [];

  for (const leaf of flatten(ctx.section.value)) {
    const name = leaf.path.split('.').pop() ?? leaf.path;
    const path = `${ctx.section.key}.${leaf.path}`;

    ctx.used.add(path);

    if (HIDDEN.has(name) || (name === 'deletedAt' && leaf.value === null)) {
      continue;
    }

    rows.push(
      ctx.masked.has(path)
        ? { label: labelOf(name), value: MASKED, masked: true }
        : { label: labelOf(name), value: formatScalar(leaf.path, leaf.value), mono: name === 'clientId' || name.endsWith('ClientId') },
    );
  }

  return rows.concat(otherSections(ctx, payload));
};

/** Enveloppes que le presentateur n'a pas lues (`movement` d'un lot cree a la main) : rendues telles quelles, jamais perdues. */
const otherSections = (ctx: Context, payload: unknown): readonly DetailRow[] => {
  if (!isRecord(payload)) {
    return [];
  }

  const rows: DetailRow[] = [];

  for (const [key, value] of Object.entries(payload)) {
    if (key === ctx.section.key || ctx.used.has(key)) {
      continue;
    }

    for (const leaf of flatten(value, key)) {
      const name = leaf.path.split('.').pop() ?? leaf.path;

      ctx.used.add(leaf.path);

      if (HIDDEN.has(name)) {
        continue;
      }

      rows.push(
        ctx.masked.has(leaf.path)
          ? { label: leaf.path, value: MASKED, masked: true }
          : { label: leaf.path, value: formatScalar(leaf.path, leaf.value) },
      );
    }
  }

  return rows;
};

// ---------------------------------------------------------------------------
// Point d'entree
// ---------------------------------------------------------------------------

const UNRECOGNISED: Summary = { when: null, nature: null, detail: 'Forme de charge utile inattendue' };

interface Dedicated {
  readonly envelopes: readonly string[];
  readonly summarize: (section: Section, payload: unknown) => Summary;
  readonly rows: (ctx: Context, payload: unknown) => readonly DetailRow[];
}

const DEDICATED: Readonly<Record<string, Dedicated>> = {
  feeding_session: { envelopes: ['session'], summarize: (s) => feedingSummary(s.value), rows: (c) => feedingRows(c) },
  pumping_session: { envelopes: ['session'], summarize: (s) => pumpingSummary(s.value), rows: (c) => pumpingRows(c) },
  milk_batch: { envelopes: ['batch', 'revision'], summarize: (s) => batchSummary(s), rows: (c) => batchRows(c) },
  stock_movement: { envelopes: ['movement'], summarize: (s, p) => movementSummary(s.value, p), rows: (c, p) => movementRows(c, p) },
};

const present = (type: string, payload: unknown, redactedFields: readonly string[]): Presentation => {
  const dedicated = DEDICATED[type];
  const generic = GENERIC[type];
  const envelopes = dedicated?.envelopes ?? (generic === undefined ? ['record', 'session', 'measurement', 'profile', 'location', 'batch', 'movement'] : [generic.envelope]);
  const section = sectionOf(payload, envelopes);

  if (section === null) {
    return { recognised: false, summary: UNRECOGNISED, rows: [] };
  }

  const ctx: Context = { section, masked: new Set(redactedFields), used: new Set() };
  const rows = [...(dedicated === undefined ? genericRows(ctx, payload) : dedicated.rows(ctx, payload)), ...(dedicated === undefined ? [] : otherSections(ctx, payload))];

  // Un champ masque que le presentateur n'a pas rendu reste annonce : le
  // retirer des lignes laisserait croire qu'il n'existe pas.
  const remaining = redactedFields
    .filter((path) => !ctx.used.has(path))
    .map((path): DetailRow => ({ label: labelOf(path.split('.').pop() ?? path), value: MASKED, masked: true }));

  const summary =
    dedicated !== undefined
      ? dedicated.summarize(section, payload)
      : {
          when: generic?.whenKey === null || generic === undefined ? null : stringOf(section.value, generic.whenKey),
          nature: null,
          detail: generic?.detail(section.value) ?? firstText(section.value, ['name', 'firstName', 'label', 'code', 'type']) ?? `${String(Object.keys(section.value).length)} champs`,
        };

  return { recognised: true, summary, rows: [...rows, ...remaining] };
};

/**
 * Presentation d'une charge utile, **sans jamais lever**. Une erreur imprevue
 * d'un presentateur retombe sur `recognised: false` : l'ecran montre le JSON brut
 * au lieu de casser la page.
 */
export const presentRecord = (type: string, payload: unknown, redactedFields: readonly string[] = []): Presentation => {
  try {
    return present(type, payload, redactedFields);
  } catch {
    return { recognised: false, summary: UNRECOGNISED, rows: [] };
  }
};

const ENUMS_BY_FIELD: Readonly<Record<string, Readonly<Record<string, string>>>> = {
  milkType: MILK_TYPES,
  breastOrder: BREAST_ORDERS,
  startingSide: { left: 'Gauche', right: 'Droite' },
  status: BATCH_STATUSES,
  category: CATEGORIES,
  origin: ORIGINS,
  type: {
    bottle: 'Biberon',
    breastfeeding: 'Tétée',
    ...Object.fromEntries(Object.entries(MOVEMENT_TYPES).map(([key, { label }]) => [key, label])),
  },
};

/** Valeur d'une difference entre versions : une enum se lit en francais, le reste comme ailleurs. */
export const formatChangeValue = (path: string, value: Scalar): string => {
  const name = path.split('.').pop() ?? path;
  const table = ENUMS_BY_FIELD[name];

  return typeof value === 'string' && table?.[value] !== undefined ? table[value] : formatScalar(path, value);
};

/** Libelle d'un chemin de champ pour une difference entre versions (`session.consumedQuantityMl`). */
export const changeLabel = (path: string): string => {
  const parts = path.split('.');
  const leaf = parts[parts.length - 1] ?? path;
  const known = DIFF_LABELS[leaf] ?? FIELD_LABELS[leaf];

  return known ?? leaf;
};

const DIFF_LABELS: Readonly<Record<string, string>> = {
  offeredQuantityMl: 'Quantité proposée',
  consumedQuantityMl: 'Quantité bue',
  remainingQuantityMl: 'Quantité restante',
  initialQuantityMl: 'Quantité initiale',
  quantityMl: 'Quantité',
  leftQuantityMl: 'Quantité gauche',
  rightQuantityMl: 'Quantité droite',
  totalQuantityMl: 'Quantité totale',
  leftDurationSeconds: 'Durée sein gauche',
  rightDurationSeconds: 'Durée sein droit',
  durationSeconds: 'Durée',
  startedAt: 'Début',
  endedAt: 'Fin',
  pumpedAt: 'Date du lait',
  expiresAt: 'Date limite',
  milkType: 'Type de lait',
  breastOrder: 'Côté',
  startingSide: 'Sein de départ',
  contentLabel: 'Contenu',
  portionWeightGrams: 'Poids d’une portion',
  storageLocationClientId: 'Lieu de stockage',
};

const OPERATIONS: Readonly<Record<string, string>> = {
  create: 'Création',
  update: 'Modification',
  delete: 'Suppression',
  // Le mobile nomme l'operation d'une seance par sa nature, et celle d'un mouvement par son type.
  bottle: 'Création du biberon',
  breastfeeding: 'Création de la tétée',
};

/** Libelle d'une operation du journal (`create`, `bottle`, `consumption`…) ; l'inconnue reste lisible. */
export const operationLabel = (operationType: string): string =>
  OPERATIONS[operationType] ?? MOVEMENT_TYPES[operationType]?.label ?? operationType;
